/**
 * `attempt-gate` — "run this at most once per key, unless the run never
 * happened."
 *
 * Extracted from `useAskUserRehydrate`, where the naive form of this rule
 * shipped a defect (`ec7ba502c`, found by `ui-tester` 2026-09-06). That hook
 * latched a ref BEFORE its first `await` and checked its `cancelled` flag
 * AFTER, so under React StrictMode — which double-invokes effects in dev —
 * the sequence was:
 *
 *   pass 1 latches, starts the read → cleanup sets cancelled → pass 2 sees the
 *   latch and returns WITHOUT reading → pass 1 resolves and bails on cancelled
 *
 * ⇒ the read fired once and its result was thrown away, so no card ever
 * rendered. The latch is correct in intent (do not re-raise a card the lawyer
 * has already dismissed) and wrong in bookkeeping: an ABANDONED attempt had
 * permanently consumed the one attempt it never completed.
 *
 * ## Why this is a module and not four lines in the hook
 *
 * lexide's vitest runs `environment: "node"` with no jsdom on purpose
 * (`vitest.config.ts`: rendering belongs in Playwright). A mount/cleanup/mount
 * ordering bug is therefore untestable in the hook itself, and the house idiom
 * — mirroring the logic inside the test file — would mean asserting against a
 * COPY of the very ordering that was wrong. A drifting mirror is a false green.
 * So the decision lives here, as pure functions over a value, and BOTH the hook
 * and the test use the same code.
 *
 * ## The token, which is not ceremony
 *
 * Every attempt carries its own token and `settle`/`abandon` are no-ops unless
 * the caller still OWNS the current attempt. Without that, the abandoned pass's
 * late `finally` would settle the attempt started by the pass that replaced it
 * — marking a still-in-flight read as finished, and re-arming the same class of
 * bug one level down. Keying ownership on the caseId alone cannot express this,
 * because both passes carry the same caseId.
 */

/**
 * The single attempt in progress, or `null` when the key is free.
 *
 * `settled: true` means the attempt produced its outcome — success, empty, or
 * error alike. It is deliberately NOT "succeeded": an attempt that ran and
 * found no parked mission must still consume the key, or every re-render would
 * re-ask the server the same question.
 */
export type AttemptRecord = { key: string; token: symbol; settled: boolean } | null;

/**
 * Claim the one attempt for `key`.
 *
 * `proceed: false` means someone already holds it — settled or not. An
 * unsettled holder is NOT stolen from: two live readers for one key is the race
 * this gate exists to prevent, and the abandoning pass releases the key itself
 * through {@link abandonAttempt}.
 */
export function beginAttempt(
  state: AttemptRecord,
  key: string,
  token: symbol,
): { proceed: boolean; next: AttemptRecord } {
  if (state && state.key === key) return { proceed: false, next: state };
  return { proceed: true, next: { key, token, settled: false } };
}

/**
 * Mark the attempt finished, so the key stays consumed for good.
 *
 * A no-op unless `token` still owns the current attempt — see the module
 * header on why a late finaliser must not settle its successor's work.
 */
export function settleAttempt(state: AttemptRecord, token: symbol): AttemptRecord {
  if (!state || state.token !== token) return state;
  if (state.settled) return state;
  return { ...state, settled: true };
}

/**
 * Release the key IF this attempt was torn down before it settled.
 *
 * A settled attempt is never released — that is the half of the original latch
 * worth keeping, and it is what stops a dismissed card from being re-raised.
 */
export function abandonAttempt(state: AttemptRecord, token: symbol): AttemptRecord {
  if (!state || state.token !== token) return state;
  if (state.settled) return state;
  return null;
}
